import type { ClientDeleteOneResponse } from "api-types/common/commonResponses";
import type {
  ClientLocationPlan,
  ClientLocationPlanBaseData,
  ClientLocationPlanBrochureSource,
  ClientLocationPlanBrochureSourcesData,
  ClientLocationPlanHydrated,
} from "api-types/locationPlans/locationPlansResponses";
import type { OptionEffectOverrides } from "building-types/options/optionEffect";
import type { OptionSetOverrides } from "building-types/options/optionTemplateTypes";
import { WEB_API_BASE_PATH } from "common/constants";
import type { FetchErrorResponse } from "common/utils/fetchHelpers";
import { handleFetchError } from "common/utils/fetchHelpers";
import type { NullablePartial } from "common/utils/types";
import {
  getBuilderAccountBrochureSourcesURL,
  getBuilderAccountMasterPlanBrochureSourceURL,
  getBuilderLocationBrochureSourcesURL,
  getBuilderLocationPlanBrochureSourceURL,
} from "lib/endpointHelpers";
import { deleteWithTimeout, getWithTimeout, postWithTimeout, putWithTimeout } from "lib/utils/fetchAuthHelpers";
import type { StorageResponse } from "./types";

function baseUrl({ builderId, locationId }): string {
  return `${WEB_API_BASE_PATH}/builders/${builderId}/locations-internal/${locationId}/location-plans`;
}

export async function updateLocationPlanOverrides(
  builderId: string,
  locationId: string,
  locationPlanId: string,
  overrides: OptionSetOverrides,
  optionEffectOverrides: OptionEffectOverrides | undefined,
) {
  await putWithTimeout(
    `${baseUrl({ builderId, locationId })}/${locationPlanId}`,
    { overrides, optionEffectOverrides },
    30 * 1000,
  );
}

export async function getLocationPlanBaseData(
  builderId: string,
  locationId: string,
  locationPlanId: string,
): StorageResponse<ClientLocationPlanBaseData> {
  try {
    const res = await getWithTimeout(`${baseUrl({ builderId, locationId })}/${locationPlanId}`);
    const json = await res.json();
    if (!res.ok) {
      throw json;
    }
    return [json as ClientLocationPlanBaseData, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function getHydratedLocationPlan(
  builderId: string,
  locationId: string,
  locationPlanId: string,
): StorageResponse<ClientLocationPlanHydrated> {
  try {
    const res = await getWithTimeout(`${baseUrl({ builderId, locationId })}/${locationPlanId}/hydrated`);
    const json = await res.json();
    if (!res.ok) {
      throw json;
    }
    return [json as ClientLocationPlanHydrated, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function putLocationPlan(
  builderId: string,
  locationId: string,
  locationPlanId: string,
  locationPlan: NullablePartial<ClientLocationPlan>,
): StorageResponse<ClientLocationPlan> {
  try {
    const res = await putWithTimeout(`${baseUrl({ builderId, locationId })}/${locationPlanId}`, locationPlan);

    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as ClientLocationPlan, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function getAccountBrochureSources(
  builderId: string,
): StorageResponse<ClientLocationPlanBrochureSourcesData> {
  try {
    const res = await getWithTimeout(getBuilderAccountBrochureSourcesURL({ builderId }));
    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as ClientLocationPlanBrochureSourcesData, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function putMasterPlanBrochureSource(
  builderId: string,
  masterPlanId: string,
  brochureSource: ClientLocationPlanBrochureSource,
): StorageResponse<ClientLocationPlanBrochureSourcesData> {
  try {
    const res = await putWithTimeout(
      getBuilderAccountMasterPlanBrochureSourceURL({ builderId, masterPlanId }),
      brochureSource,
    );
    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as ClientLocationPlanBrochureSourcesData, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function getLocationBrochureSources(
  builderId: string,
  locationId: string,
): StorageResponse<ClientLocationPlanBrochureSourcesData> {
  try {
    const res = await getWithTimeout(getBuilderLocationBrochureSourcesURL({ builderId, locationId }));
    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as ClientLocationPlanBrochureSourcesData, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function putLocationPlanBrochureSource(
  builderId: string,
  locationId: string,
  locationPlanId: string,
  brochureSource: ClientLocationPlanBrochureSource,
): StorageResponse<ClientLocationPlanBrochureSourcesData> {
  try {
    const res = await putWithTimeout(
      getBuilderLocationPlanBrochureSourceURL({ builderId, locationId, locationPlanId }),
      brochureSource,
    );
    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as ClientLocationPlanBrochureSourcesData, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function deleteLocationPlanBrochureSource(
  builderId: string,
  locationId: string,
  locationPlanId: string,
): StorageResponse<ClientLocationPlanBrochureSourcesData> {
  try {
    const res = await deleteWithTimeout(
      getBuilderLocationPlanBrochureSourceURL({ builderId, locationId, locationPlanId }),
      null,
    );
    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as ClientLocationPlanBrochureSourcesData, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function postLocationPlan({
  builderId,
  locationId,
  masterPlanId,
  planName,
  parentId,
}: {
  builderId: string;
  locationId: string;
  masterPlanId: string;
  planName: string;
  parentId?: string;
}): StorageResponse<ClientLocationPlan> {
  try {
    const res = await postWithTimeout(
      baseUrl({ builderId, locationId }),
      { masterPlanId, name: planName, parentId },
      10 * 1000,
    );

    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function deleteCommunityPlan(
  builderId: string,
  locationId: string,
  locationPlanId: string,
): Promise<FetchErrorResponse | undefined> {
  try {
    const res = await deleteWithTimeout(`${baseUrl({ builderId, locationId })}/${locationPlanId}`, null, 10 * 1000);

    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return undefined;
  } catch (e) {
    return handleFetchError(e);
  }
}

export async function archiveCommunityPlan(
  builderId: string,
  locationId: string,
  locationPlanId: string,
): StorageResponse<ClientDeleteOneResponse, null> {
  try {
    const res = await putWithTimeout(`${baseUrl({ builderId, locationId })}/${locationPlanId}/archive`, {}, 30 * 1000);

    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as ClientDeleteOneResponse, null];
  } catch (e) {
    return [null, await handleFetchError(e)];
  }
}

export async function unarchiveCommunityPlan(
  builderId: string,
  locationId: string,
  locationPlanId: string,
): StorageResponse<ClientDeleteOneResponse, null> {
  try {
    const res = await putWithTimeout(
      `${baseUrl({ builderId, locationId })}/${locationPlanId}/unarchive`,
      {},
      30 * 1000,
    );

    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as ClientDeleteOneResponse, null];
  } catch (e) {
    return [null, await handleFetchError(e)];
  }
}
